import { createTheme } from '@mui/material/styles';

/**
 * Paleta del rediseño. Los mismos valores viven en src/styles/tokens.scss
 * como variables CSS; acá se exponen para los componentes de MUI y para JS.
 */
/**
 * Tokens de la interfaz. Apuntan a las variables CSS de src/styles/tokens.scss,
 * así que cualquier `sx={{ color: tokens.ink }}` sigue el modo claro/oscuro solo.
 * Los valores literales que MUI necesita para hacer cálculos de color viven
 * abajo, en `paletteColors`.
 */
export const tokens = {
  canvas: "var(--sup-canvas)",
  surface: "var(--sup-surface)",
  surface2: "var(--sup-surface-2)",
  border: "var(--sup-border)",
  borderSoft: "var(--sup-border-soft)",
  borderStrong: "var(--sup-border-strong)",

  ink: "var(--sup-ink)",
  ink2: "var(--sup-ink-2)",
  ink3: "var(--sup-ink-3)",
  ink4: "var(--sup-ink-4)",

  brand: "var(--sup-brand)",
  brandDark: "var(--sup-brand-dark)",
  brandDarker: "var(--sup-brand-darker)",
  brandTint: "var(--sup-brand-tint)",
  brandTint2: "var(--sup-brand-tint-2)",
  brandBorder: "var(--sup-brand-border)",

  graphite: "var(--sup-graphite)",
  graphite2: "var(--sup-graphite-2)",
  graphite3: "var(--sup-graphite-3)",
  graphiteLine: "var(--sup-graphite-line)",
  graphiteInk: "var(--sup-graphite-ink)",
  graphiteInk2: "var(--sup-graphite-ink-2)",
  graphiteInk3: "var(--sup-graphite-ink-3)",
  graphiteBadge: "var(--sup-graphite-badge)",

  radiusControl: 8,
  radiusCard: 12,

  shadowSm: "var(--sup-shadow-sm)",
  shadowMd: "var(--sup-shadow-md)",
  shadowBrand: "var(--sup-shadow-brand)",

  topbarHeight: 64,
  sidebarWidth: 248,
  railWidth: 72,
  bottomNavHeight: 68,
};

/** Literales para la paleta de MUI, que hace cálculos de color y no admite var(). */
const paletteColors = {
  brand: "#e53935",
  brandDark: "#b91411",
  graphite: "#1c1a19",
  canvas: "#f6f5f4",
  surface: "#ffffff",
  ink: "#1b1a19",
  ink2: "#615d5a",
  ink4: "#b6b1ad",
  border: "#e7e4e1",
};

const fontStack = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 768, lg: 1280, xl: 1536 },
  },
  palette: {
    primary: {
      light: '#ed6666',
      main: paletteColors.brand,
      dark: paletteColors.brandDark,
      contrastText: '#fff',
    },
    secondary: {
      light: '#4a4644',
      main: paletteColors.graphite,
      dark: '#000000',
      contrastText: '#fff',
    },
    error: { main: paletteColors.brandDark },
    warning: { main: '#8a5a12' },
    success: { main: '#3f6b1f' },
    background: { default: paletteColors.canvas, paper: paletteColors.surface },
    /*
      OJO: acá van literales a propósito. MUI hace cálculos de color sobre
      `palette.text` (por ejemplo `alpha()` en Button) y revienta si le pasás
      un `var()`. Para que el texto siga al tema se usan los styleOverrides de
      MuiTypography, más abajo, que son CSS y sí admiten variables.
    */
    text: { primary: paletteColors.ink, secondary: paletteColors.ink2, disabled: paletteColors.ink4 },
    divider: paletteColors.border,
  },
  shape: { borderRadius: tokens.radiusControl },
  typography: {
    fontFamily: fontStack,
    h1: { fontSize: '27px', fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontSize: '21px', fontWeight: 600, letterSpacing: '-0.015em' },
    h3: { fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontSize: '15px', fontWeight: 600 },
    h5: { fontSize: '14px', fontWeight: 600 },
    h6: { fontSize: '13.5px', fontWeight: 600 },
    subtitle1: { fontSize: '14px', fontWeight: 500 },
    subtitle2: { fontSize: '12.5px', fontWeight: 500, color: tokens.ink2 },
    body1: { fontSize: '14px', fontWeight: 400 },
    body2: { fontSize: '13px', fontWeight: 400 },
    caption: { fontSize: '11.5px', fontWeight: 400, color: tokens.ink3 },
    overline: {
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      color: tokens.ink3,
      textTransform: 'uppercase',
    },
    button: { textTransform: 'none', fontWeight: 500, fontSize: '13.5px' },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        /*
          Acá NO va un color en `root`. MUI no le pone color propio a Typography
          —su estilo base es sólo `margin: 0` más la variante—, así que hereda,
          y `body` ya lleva `color: var(--sup-ink)` (route.scss), que sigue el
          tema solo. Ponerlo acá era redundante y encima rompía la herencia:
          dejaba el texto casi negro sobre el menú grafito, ilegible en modo
          claro. Sólo se declaran las variantes que llevan otro tono.
        */
        subtitle2: { color: tokens.ink2 },
        caption: { color: tokens.ink3 },
        overline: { color: tokens.ink3 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: tokens.radiusControl, minHeight: 36, paddingLeft: 15, paddingRight: 15 },
        outlined: { borderColor: tokens.border, color: tokens.ink },
        text: { color: tokens.brandDark },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: tokens.radiusControl } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: tokens.radiusCard },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radiusControl,
          backgroundColor: tokens.surface,
          '& fieldset': { borderColor: tokens.border },
          '&:hover fieldset': { borderColor: tokens.borderStrong },
        },
        input: { fontSize: '13.5px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '13.5px' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500, fontSize: '11.5px', height: 24 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.graphite,
          fontSize: '12px',
          fontWeight: 400,
          borderRadius: 6,
          padding: '6px 9px',
        },
        arrow: { color: tokens.graphite },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#f5f3f1', fontSize: '13.5px' },
        head: {
          fontSize: '11px',
          fontWeight: 600,
          color: tokens.ink3,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          backgroundColor: tokens.surface2,
        },
      },
    },
  },
});

export default theme;
